import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  Upload, FileText, Link as LinkIcon, Type, ArrowLeft, 
  CheckCircle, AlertCircle, Loader2, Coins 
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import VerificationRequiredModal from "../components/VerificationRequiredModal";

const BookUpload = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token, refreshUser, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pdf");
  const [formData, setFormData] = useState({
    title: "",
    url: "",
    text: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [creditCosts, setCreditCosts] = useState({ book_analysis: 15 });
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const pollIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    // Fetch credit costs from API
    axios.get(`${API}/credits/costs`)
      .then(res => setCreditCosts(res.data))
      .catch(err => console.error("Failed to fetch costs:", err));
    
    return () => {
      if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      if (!formData.title) {
        setFormData(prev => ({ ...prev, title: file.name.replace('.pdf', '') }));
      }
    }
  }, [formData.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt']
    },
    maxFiles: 1
  });

  const startProgressAnimation = () => {
    setProgress(10);
    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        // Slowly increase progress, max out at 90% until complete
        if (prev < 30) return prev + 2;
        if (prev < 60) return prev + 1;
        if (prev < 90) return prev + 0.5;
        return prev;
      });
    }, 500);
  };

  const stopProgressAnimation = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleSubmit = async () => {
    // Check if user is verified
    if (user?.email_verified === false) {
      setShowVerificationModal(true);
      return;
    }

    if (!formData.title) {
      toast.error("Please enter a title for your document");
      return;
    }

    const analysisCredits = creditCosts.book_analysis || 15;
    if ((user?.credits || 0) < analysisCredits) {
      toast.error("Insufficient credits. Please purchase more credits.");
      navigate("/pricing");
      return;
    }

    // Reset state
    setLoading(true);
    setProgress(0);
    setStatusMessage("Uploading content...");
    hasCompletedRef.current = false;

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);

      if (activeTab === 'pdf' && selectedFile) {
        submitData.append('file', selectedFile);
      } else if (activeTab === 'url' && formData.url) {
        submitData.append('url', formData.url);
      } else if (activeTab === 'text' && formData.text) {
        submitData.append('text_content', formData.text);
      } else {
        toast.error("Please provide content to analyze");
        setLoading(false);
        return;
      }

      setProgress(5);
      setStatusMessage("Processing upload...");

      // Track upload start so we can recover if mobile connection drops mid-flight
      const uploadStartedAt = Date.now();

      // Helper: poll a book by ID until ready / error / timeout
      const pollBook = (bookId) => {
        const startedAt = Date.now();
        setStatusMessage("Analyzing content and extracting chapters...");
        startProgressAnimation();

        const tick = async () => {
          if (hasCompletedRef.current) return;
          try {
            const bookStatus = await axios.get(`${API}/books/${bookId}`, {
              headers: getAuthHeaders(token),
            });
            const status = bookStatus.data.status;
            if (status === "ready") {
              hasCompletedRef.current = true;
              stopProgressAnimation();
              setProgress(100);
              setStatusMessage("Complete!");
              await refreshUser();
              toast.success("Book analyzed successfully!");
              setTimeout(() => navigate(`/books/${bookId}/chapters`), 500);
            } else if (status === "error") {
              hasCompletedRef.current = true;
              stopProgressAnimation();
              setLoading(false);
              setProgress(0);
              toast.error(bookStatus.data.error || "Analysis failed. Please try again.");
            } else {
              pollIntervalRef.current = setTimeout(tick, 2000);
            }
          } catch (err) {
            // Network blip during polling — retry with backoff up to 3 min
            if (Date.now() - startedAt < 180000) {
              pollIntervalRef.current = setTimeout(tick, 3000);
            } else {
              hasCompletedRef.current = true;
              stopProgressAnimation();
              setLoading(false);
              toast.info("Taking longer than expected. Check your books to see if analysis completed.");
            }
          }
        };
        pollIntervalRef.current = setTimeout(tick, 2000);

        // Safety hard timeout
        setTimeout(() => {
          if (!hasCompletedRef.current) {
            hasCompletedRef.current = true;
            if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
            stopProgressAnimation();
            setLoading(false);
            setProgress(0);
            toast.info("Taking longer than expected. Check your books to see if analysis completed.");
          }
        }, 180000);
      };

      // Upload - backend now returns within ~1s (heavy parsing happens in background).
      // We keep a generous timeout for slow mobile uploads of large PDFs.
      let response;
      try {
        response = await axios.post(`${API}/books/upload`, submitData, {
          headers: {
            ...getAuthHeaders(token),
            "Content-Type": "multipart/form-data",
          },
          timeout: 180000, // 3 min for large PDFs on slow networks
        });
      } catch (uploadErr) {
        // RECOVERY: On mobile cellular, the request body often uploads successfully
        // but the response gets lost. Before showing an error, check if the server
        // actually created a book in the last minute for this user.
        console.warn("Upload request failed, attempting recovery:", uploadErr?.message);
        try {
          const recent = await axios.get(`${API}/books/recent?seconds=120`, {
            headers: getAuthHeaders(token),
          });
          const recentList = recent.data || [];
          const recovered = recentList.find((b) => {
            try {
              const created = new Date(b.created_at).getTime();
              return created >= uploadStartedAt - 5000; // created at or after we hit submit
            } catch (e) {
              return false;
            }
          });
          if (recovered) {
            toast.info("Connection blip — your book is still being analyzed. Continuing...");
            await refreshUser();
            pollBook(recovered.book_id);
            return;
          }
        } catch (recoverErr) {
          // ignore — fall through to error handler
        }
        throw uploadErr;
      }

      const bookId = response.data.book_id;
      pollBook(bookId);
      return;
      
    } catch (error) {
      console.error("Book upload error:", error);
      stopProgressAnimation();
      setLoading(false);
      setProgress(0);
      
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out. Please try again.");
      } else if (error.response?.status === 402) {
        toast.error("Insufficient credits. Please purchase more credits.");
        navigate("/pricing");
      } else {
        toast.error(error.response?.data?.detail || "Failed to upload book");
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardHeader />

      <main className="container-main py-8">
        <Button 
          variant="ghost" 
          className="mb-6 gap-2"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <div className="max-w-2xl mx-auto">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Upload Content
              </CardTitle>
              <CardDescription>
                Upload a PDF, paste a URL, or enter text directly. Our AI will analyze it and extract chapters.
              </CardDescription>
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-500 bg-slate-50 p-2 rounded-lg">
                <Coins className="w-4 h-4 text-[#F97316]" />
                <span>Cost: {creditCosts.book_analysis || 15} credits for analysis</span>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Title Input */}
              <div className="space-y-2">
                <Label htmlFor="title">Document Title</Label>
                <Input
                  id="title"
                  placeholder="Enter a title for your document"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="h-12"
                  data-testid="title-input"
                  disabled={loading}
                />
              </div>

              {/* Content Source Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="pdf" className="gap-2" data-testid="tab-pdf" disabled={loading}>
                    <FileText className="w-4 h-4" />
                    PDF File
                  </TabsTrigger>
                  <TabsTrigger value="url" className="gap-2" data-testid="tab-url" disabled={loading}>
                    <LinkIcon className="w-4 h-4" />
                    URL
                  </TabsTrigger>
                  <TabsTrigger value="text" className="gap-2" data-testid="tab-text" disabled={loading}>
                    <Type className="w-4 h-4" />
                    Text
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="pdf">
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      isDragActive 
                        ? 'border-[#4F46E5] bg-indigo-50' 
                        : selectedFile 
                        ? 'border-green-500 bg-green-50'
                        : 'border-slate-200 hover:border-[#4F46E5] hover:bg-slate-50'
                    } ${loading ? 'pointer-events-none opacity-60' : ''}`}
                    data-testid="dropzone"
                  >
                    <input {...getInputProps()} data-testid="file-input" disabled={loading} />
                    {selectedFile ? (
                      <div className="flex flex-col items-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
                        <p className="font-medium text-slate-900">{selectedFile.name}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="mt-2 text-slate-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                          }}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className={`w-12 h-12 mx-auto mb-3 ${
                          isDragActive ? 'text-[#4F46E5]' : 'text-slate-400'
                        }`} />
                        <p className="font-medium text-slate-900">
                          {isDragActive ? 'Drop your file here' : 'Drag & drop your PDF here'}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                          or click to browse (PDF, TXT)
                        </p>
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="url">
                  <div className="space-y-2">
                    <Label htmlFor="url">URL</Label>
                    <div className="relative">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="url"
                        type="url"
                        placeholder="https://example.com/article"
                        value={formData.url}
                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        className="pl-10 h-12"
                        data-testid="url-input"
                        disabled={loading}
                      />
                    </div>
                    <p className="text-sm text-slate-500">
                      Enter the URL of an article, webpage, or online document
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="text">
                  <div className="space-y-2">
                    <Label htmlFor="text">Content</Label>
                    <Textarea
                      id="text"
                      placeholder="Paste or type your content here..."
                      value={formData.text}
                      onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                      className="min-h-[200px] resize-none"
                      data-testid="text-input"
                      disabled={loading}
                    />
                    <p className="text-sm text-slate-500">
                      {formData.text.length} characters
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Progress indicator when uploading/analyzing */}
              {loading && (
                <div className="space-y-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-medium">{statusMessage}</span>
                    <span className="text-orange-600 font-medium">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-slate-500">
                    This may take up to a minute depending on the document length.
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full btn-accent h-12"
                data-testid="upload-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload & Analyze
                  </>
                )}
              </Button>

              {/* Info Box */}
              <div className="bg-slate-50 rounded-xl p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900 mb-1">What happens next?</p>
                  <p>Our AI will analyze your content, identify chapters or sections, and extract key topics. This usually takes 30-60 seconds depending on the document length.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Verification Required Modal */}
      <VerificationRequiredModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        userEmail={user?.email}
      />
    </div>
  );
};

export default BookUpload;
